import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";
import {
  FiAlertCircle,
  FiBriefcase,
  FiCheck,
  FiChevronDown,
  FiChevronRight,
  FiFileText,
  FiLayers,
  FiMail,
  FiPause,
  FiSearch,
  FiUsers,
  FiX,
} from "react-icons/fi";
import { fetchWithAuth } from "../utils/authFetch";
import { creditsChanged } from "../utils/useCredits";
import { useFindNotification } from "../context/FindNotificationContext";
import { timeAgo } from "../utils/timeAgo";

// Activities: long-running work the user started (imports, tracking, matching…), from /api/activity.
const POLL_BUSY_MS = 3000;

const ActivityContext = createContext(null);

/* One poller per tab, shared by the tray and the Dashboard. It asks /api/activity only while
   something is running (every POLL_BUSY_MS); idle it stays quiet and wakes on a job being queued
   (fetchWithAuth fires "activity:changed"), the tab becoming visible, or a route change. */
export function ActivityProvider({ children }) {
  const [jobs, setJobs] = useState([]);
  const [running, setRunning] = useState(0);
  const runningRef = useRef(0);
  const timerRef = useRef(null);
  const { pathname } = useLocation();

  const load = useCallback(async () => {
    try {
      const res = await fetchWithAuth("/api/activity");
      if (!res.ok) return;
      const data = await res.json();
      const list = Array.isArray(data?.jobs) ? data.jobs : [];
      setJobs(list);
      const nowRunning = Number(data?.running) || 0;
      // A run that just finished has charged credits: let the sidebar balance catch up.
      if (nowRunning < runningRef.current) creditsChanged();
      runningRef.current = nowRunning;
      setRunning(nowRunning);
    } catch (_) {
      // transient — next tick retries
    }
  }, []);

  useEffect(() => {
    let stopped = false;
    // Poll while jobs run; the chain ends by itself once nothing is running.
    const tick = async () => {
      await load();
      if (stopped) return;
      clearTimeout(timerRef.current);
      if (runningRef.current > 0) timerRef.current = setTimeout(tick, POLL_BUSY_MS);
    };
    const wake = () => {
      clearTimeout(timerRef.current);
      tick();
    };
    const onVisible = () => document.visibilityState === "visible" && wake();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("activity:changed", wake);
    wake();
    return () => {
      stopped = true;
      clearTimeout(timerRef.current);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("activity:changed", wake);
    };
  }, [load]);

  // Opening another page is a natural moment to catch work started elsewhere.
  useEffect(() => {
    if (runningRef.current === 0) window.dispatchEvent(new Event("activity:changed"));
  }, [pathname]);

  const value = useMemo(() => ({ jobs, running, refresh: load }), [jobs, running, load]);
  return <ActivityContext.Provider value={value}>{children}</ActivityContext.Provider>;
}

export function useActivity() {
  const ctx = useContext(ActivityContext);
  if (!ctx) throw new Error("useActivity must be used within ActivityProvider");
  return ctx;
}

// "12 of 20 strong candidates" when the job names its unit, else "12 / 20".
function progressText(job) {
  const p = job.progress;
  if (!p || !(p.total > 0)) return "";
  return p.unit ? `${p.done} of ${p.total} ${p.unit}` : `${p.done} / ${p.total}`;
}

function durationOf(job) {
  if (!job.startedAt || !job.finishedAt) return "";
  const s = Math.round((new Date(job.finishedAt) - new Date(job.startedAt)) / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

const sweep = keyframes`
  from { left: -40%; }
  to { left: 100%; }
`;

const Bar = styled.div`
  height: 6px;
  border-radius: 4px;
  background: var(--line-soft);
  overflow: hidden;
  position: relative;
  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: ${(p) => (p.$indeterminate ? "40%" : `${p.$pct || 0}%`)};
    border-radius: 4px;
    background: ${(p) =>
      p.$status === "failed" ? "var(--danger)" : p.$status === "succeeded" ? "var(--success)" : "var(--accent)"};
    transition: width 0.4s ease;
    animation: ${(p) => (p.$indeterminate ? sweep : "none")} 1.4s linear infinite;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 18px 1fr auto;
  gap: 10px;
  align-items: start;
  padding: 10px 0;
  border-bottom: 1px solid var(--line-soft);
  &:last-child {
    border-bottom: 0;
  }
  .t {
    font-size: 13px;
    font-weight: 600;
    color: var(--ink);
    line-height: 1.3;
  }
  .m {
    font-size: 12px;
    color: var(--ink-muted);
    margin-top: 2px;
    line-height: 1.35;
  }
  .when {
    font-size: 11.5px;
    color: var(--ink-muted);
    white-space: nowrap;
    padding-top: 2px;
  }
  .bar {
    margin-top: 7px;
  }
`;

const Dot = styled.span`
  display: inline-grid;
  place-items: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  margin-top: 1px;
  color: var(--on-fill, #fff);
  background: ${(p) =>
    p.$status === "failed" ? "var(--danger)" : p.$status === "succeeded" ? "var(--success)" : "var(--accent)"};
  ${(p) =>
    p.$status === "running" || p.$status === "queued"
      ? "background: transparent; border: 2px solid var(--accent); border-top-color: transparent; animation: spin 0.9s linear infinite;"
      : ""}
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Open = styled.button`
  border: 0;
  background: none;
  color: var(--accent);
  font: inherit;
  font-size: 12px;
  line-height: 1;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 2px;
`;

// The row's secondary actions (file toggle, open link) sit on one baseline, split by a hairline.
const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 6px;
  .sep {
    width: 1px;
    height: 11px;
    background: var(--line-strong);
  }
`;

const Items = styled.div`
  margin-top: 6px;
  border-left: 2px solid var(--line-soft);
  padding-left: 8px;
  .it {
    display: grid;
    grid-template-columns: 12px 1fr;
    gap: 6px;
    align-items: start;
    font-size: 12px;
    color: var(--ink-muted);
    padding: 3px 0;
    line-height: 1.3;
  }
  .it .n {
    color: var(--ink);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .it .s {
    font-size: 11.5px;
  }
`;
const ItemDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 4px;
  background: ${(p) =>
    p.$status === "failed" ? "var(--danger)" : p.$status === "succeeded" ? "var(--success)" : "var(--accent)"};
  ${(p) => (p.$status === "queued" ? "opacity: 0.35;" : "")}
`;

// One job, shared by the tray and the Dashboard card. A batched upload carries its
// files as `items`, shown behind a toggle so the list stays one row per upload.
export function ActivityRow({ job, showWhen = true }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const items = Array.isArray(job.items) ? job.items : [];
  const active = job.status === "running" || job.status === "queued";
  const pct = job.progress && job.progress.total > 0 ? Math.round((100 * job.progress.done) / job.progress.total) : null;
  const detail = active
    ? [job.status === "queued" ? "Waiting to start" : job.stage, progressText(job) || null]
        .filter(Boolean)
        .join(" · ")
    : job.message;
  return (
    <Row>
      <Dot $status={job.status}>
        {job.status === "succeeded" && <FiCheck size={11} />}
        {job.status === "failed" && <FiAlertCircle size={11} />}
      </Dot>
      <div style={{ minWidth: 0 }}>
        <div className="t">{job.label}</div>
        {detail && <div className="m">{detail}</div>}
        {active && (
          <div className="bar">
            <Bar $status={job.status} $pct={pct ?? 0} $indeterminate={pct == null || job.status === "queued"} />
          </div>
        )}
        {(items.length > 0 || (showWhen && !active && job.link)) && (
          <Actions>
            {items.length > 0 && (
              <Open onClick={() => setOpen((v) => !v)} aria-expanded={open}>
                {open ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />}
                {items.length} {items.length === 1 ? "file" : "files"}
              </Open>
            )}
            {showWhen && items.length > 0 && !active && job.link && <span className="sep" aria-hidden="true" />}
            {showWhen && !active && job.link && (
              <Open onClick={() => navigate(job.link)}>
                Open <FiChevronRight size={12} />
              </Open>
            )}
          </Actions>
        )}
        {open && items.length > 0 && (
          <Items>
            {items.map((it) => (
              <div className="it" key={it.id}>
                <ItemDot $status={it.status} />
                <div style={{ minWidth: 0 }}>
                  <div className="n" title={it.label}>{it.label}</div>
                  {it.message && <div className="s">{it.message}</div>}
                </div>
              </div>
            ))}
          </Items>
        )}
      </div>
      {/* Without a time column (the tray), "Open" takes the right edge instead of its own line. */}
      {!showWhen && !active && job.link && (
        <Open onClick={() => navigate(job.link)} style={{ paddingTop: 2 }}>
          Open <FiChevronRight size={12} />
        </Open>
      )}
      {showWhen && (
        <div className="when">
          {active ? timeAgo(job.startedAt || job.createdAt) : `${timeAgo(job.finishedAt)}${durationOf(job) ? ` · ${durationOf(job)}` : ""}`}
        </div>
      )}
    </Row>
  );
}

const isActive = (j) => j.status === "running" || j.status === "queued";
const pctOf = (j) => (j.progress && j.progress.total > 0 ? Math.round((100 * j.progress.done) / j.progress.total) : null);

/* Collapsed, the tray is the scoutm8 lens (no handle) in the corner: its ring is the progress ring (blue while work runs,
   green when done, red on a failure) and the lens sways gently while it searches. Clicking it opens the list. */
const FAB_SIZE = 56;
const LENS_R = 17; // the logo's lens ring, centred in the disc
const LENS_CX = FAB_SIZE / 2;
const LENS_CY = FAB_SIZE / 2;
const LENS_C = 2 * Math.PI * LENS_R;

const pop = keyframes`
  from { opacity: 0; transform: scale(0.6); }
  to { opacity: 1; transform: none; }
`;
const sway = keyframes`
  0%, 100% { transform: rotate(-7deg); }
  50% { transform: rotate(7deg); }
`;
const orbit = keyframes`
  to { transform: rotate(360deg); }
`;
const panelIn = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.98); }
  to { opacity: 1; transform: none; }
`;

// The lens has four looks: running (sways; ring fills, or orbits when no job reports a count), waiting (a paused
// search: still, full blue ring), done (green) and failed (red).
const RING_COLOR = { running: "var(--brand-blue)", waiting: "var(--brand-blue)", done: "var(--success)", failed: "var(--danger)" };

const Fab = styled.button`
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 90;
  width: ${FAB_SIZE}px;
  height: ${FAB_SIZE}px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid var(--line);
  background: var(--surface);
  box-shadow: var(--shadow-md);
  display: grid;
  place-items: center;
  cursor: pointer;
  animation: ${pop} 220ms cubic-bezier(0.2, 0.9, 0.3, 1.3);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: var(--shadow-lg);
  }
  &:focus-visible {
    outline: 2px solid var(--accent-ring);
    outline-offset: 2px;
  }
  ${(p) => (p.$open ? "box-shadow: 0 0 0 3px var(--accent-soft), var(--shadow-md);" : "")}

  .mark {
    transform-origin: ${LENS_CX}px ${LENS_CY}px;
    ${(p) =>
      p.$look === "running" &&
      css`
        animation: ${sway} 2.6s ease-in-out infinite;
      `}
  }
  .track {
    fill: none;
    stroke: var(--line-soft);
    stroke-width: 3.5;
  }
  .arc {
    fill: none;
    stroke: ${(p) => RING_COLOR[p.$look]};
    stroke-width: 3.5;
    stroke-linecap: round;
    transition: stroke-dashoffset 0.5s ease, stroke 0.3s ease;
  }
  .arc.orbit {
    transform-origin: ${LENS_CX}px ${LENS_CY}px;
    animation: ${orbit} 1.4s linear infinite;
  }
  .person {
    fill: var(--brand-orange);
  }
  .badge {
    position: absolute;
    top: -2px;
    right: -2px;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    background: ${(p) => RING_COLOR[p.$look]};
    color: var(--on-fill, #fff);
    font-size: 11px;
    font-weight: 700;
    line-height: 18px;
    border: 2px solid var(--surface);
    box-sizing: content-box;
    display: grid;
    place-items: center;
  }

  @media (prefers-reduced-motion: reduce) {
    .mark,
    .arc.orbit {
      animation: none;
    }
  }
  @media (max-width: 450px) {
    right: 12px;
    bottom: 12px;
  }
`;

// The logo's lens: the progress ring and the orange person inside.
function LensMark({ look, pct }) {
  const unknown = look === "running" && pct == null;
  const offset = look !== "running" ? 0 : unknown ? LENS_C * 0.72 : LENS_C * (1 - pct / 100);
  return (
    <svg viewBox={`0 0 ${FAB_SIZE} ${FAB_SIZE}`} width={FAB_SIZE} height={FAB_SIZE} aria-hidden="true">
      <g className="mark">
        <circle className="track" cx={LENS_CX} cy={LENS_CY} r={LENS_R} />
        <circle
          className={`arc${unknown ? " orbit" : ""}`}
          cx={LENS_CX}
          cy={LENS_CY}
          r={LENS_R}
          strokeDasharray={LENS_C}
          strokeDashoffset={offset}
          transform={unknown ? undefined : `rotate(-90 ${LENS_CX} ${LENS_CY})`}
        />
        <circle className="person" cx={LENS_CX} cy={LENS_CY - 4} r={4.2} />
        <path className="person" d={`M ${LENS_CX - 8} ${LENS_CY + 6.5} Q ${LENS_CX} ${LENS_CY - 1} ${LENS_CX + 8} ${LENS_CY + 6.5} Q ${LENS_CX} ${LENS_CY + 12} ${LENS_CX - 8} ${LENS_CY + 6.5} Z`} />
      </g>
    </svg>
  );
}

// The list opens above the lens, anchored to its right edge.
const Panel = styled.div`
  position: fixed;
  right: 20px;
  bottom: ${FAB_SIZE + 30}px;
  z-index: 89;
  width: 380px;
  max-height: min(60vh, 540px);
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  background: var(--surface);
  border: 1px solid var(--line);
  box-shadow: var(--shadow-lg);
  transform-origin: bottom right;
  animation: ${panelIn} 180ms ease-out;
  color: var(--ink);
  overflow: hidden;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
  @media (max-width: 450px) {
    left: 12px;
    right: 12px;
    bottom: ${FAB_SIZE + 24}px;
    width: auto;
    max-height: 55vh;
  }
`;

const PanelHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 10px 12px 18px;
  border-bottom: 1px solid var(--line-soft);
  h2 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: var(--ink);
  }
  .pill {
    padding: 2px 9px;
    border-radius: 99px;
    font-size: 12px;
    font-weight: 600;
    background: ${(p) => (p.$look === "failed" ? "var(--danger-soft)" : p.$look === "done" ? "var(--success-soft)" : "var(--accent-soft)")};
    color: ${(p) => (p.$look === "failed" ? "var(--danger)" : p.$look === "done" ? "var(--success)" : "var(--accent)")};
  }
  .close {
    margin-left: auto;
    width: 30px;
    height: 30px;
    border: 0;
    border-radius: 8px;
    background: none;
    color: var(--ink-muted);
    display: grid;
    place-items: center;
    cursor: pointer;
  }
  .close:hover {
    background: var(--bg-2);
    color: var(--ink);
  }
  .close:focus-visible {
    outline: 2px solid var(--accent-ring);
  }
`;

const PanelList = styled.div`
  overflow-y: auto;
  padding: 8px 10px 14px;
`;

// "In progress" / "Recently finished": a quiet label over each group.
const Group = styled.div`
  padding: 12px 12px 6px;
  font-size: 12px;
  font-weight: 500;
  color: var(--ink-muted);
`;

// One job in the tray: what it is, where it has got to, and (once done) a click through to it.
const Item = styled.div`
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr) auto;
  gap: 14px;
  align-items: center;
  padding: 14px 12px;
  border-radius: 12px;
  & + & {
    margin-top: 2px;
  }
  ${(p) => (p.$clickable ? "cursor: pointer;" : "")}
  &:hover {
    ${(p) => (p.$clickable ? "background: var(--bg-2);" : "")}
  }
  &:focus-visible {
    outline: 2px solid var(--accent-ring);
    outline-offset: -2px;
  }
  .icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    display: grid;
    place-items: center;
    background: ${(p) => (p.$status === "failed" ? "var(--danger-soft)" : p.$status === "succeeded" ? "var(--success-soft)" : "var(--accent-soft)")};
    color: ${(p) => (p.$status === "failed" ? "var(--danger)" : p.$status === "succeeded" ? "var(--success)" : "var(--accent)")};
  }
  .label {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--ink);
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    margin-top: 5px;
    font-size: 12.5px;
    color: var(--ink-muted);
    line-height: 1.35;
  }
  .row .count {
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
    color: var(--ink);
    font-weight: 500;
  }
  .meter {
    margin-top: 10px;
    height: 4px;
    border-radius: 99px;
    background: var(--line-soft);
    overflow: hidden;
    position: relative;
  }
  .meter > span {
    position: absolute;
    inset: 0 auto 0 0;
    border-radius: inherit;
    background: var(--brand-blue);
    transition: width 0.5s ease;
  }
  .meter.unknown > span {
    width: 35%;
    animation: ${sweep} 1.4s ease-in-out infinite;
  }
  .go {
    color: var(--ink-muted);
  }
  @media (prefers-reduced-motion: reduce) {
    .meter.unknown > span {
      animation: none;
      width: 100%;
      opacity: 0.35;
    }
  }
`;

const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

const KIND_ICON = {
  find_candidates: FiSearch,
  track_company: FiBriefcase,
  job_import: FiFileText,
  candidate_import: FiFileText,
  candidate_match: FiUsers,
  reverse_match: FiUsers,
  find_emails: FiMail,
  profile: FiBriefcase,
  embed: FiLayers,
};

function TrayItem({ job, title, onOpen }) {
  const active = isActive(job);
  const pct = pctOf(job);
  const Icon =
    job.status === "failed" ? FiAlertCircle : job.status === "paused" ? FiPause : job.status === "succeeded" ? FiCheck : KIND_ICON[job.kind] || FiLayers;
  const step = active ? (job.status === "queued" ? "Waiting to start" : job.stage) : job.message;
  const count = active && job.progress?.total > 0 ? `${job.progress.done} of ${job.progress.total}` : "";
  const clickable = !active && !!job.link;
  return (
    <Item
      $status={job.status}
      $clickable={clickable}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => onOpen(job.link) : undefined}
      onKeyDown={clickable ? (e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onOpen(job.link)) : undefined}
    >
      <span className="icon" aria-hidden="true">
        <Icon size={17} />
      </span>
      <div style={{ minWidth: 0 }}>
        <div className="label" title={title || job.label}>{title || job.label}</div>
        {(step || count) && (
          <div className="row">
            <span>{step}</span>
            {count && <span className="count">{count}</span>}
          </div>
        )}
        {active && (
          <div className={`meter${pct == null || job.status === "queued" ? " unknown" : ""}`}>
            <span style={pct == null ? undefined : { width: `${pct}%` }} />
          </div>
        )}
      </div>
      {clickable ? <FiChevronRight className="go" size={16} aria-hidden="true" /> : <span />}
    </Item>
  );
}

// The circle shows a finished job this long, then clears it; the open list keeps it for RECENT_MS.
const DONE_MS = 10 * 1000;
const RECENT_MS = 3 * 60 * 1000;

const decodePath = (p) => {
  try {
    return decodeURIComponent(p || "");
  } catch {
    return p || "";
  }
};

// True when the current page already shows this job's progress itself, so the tray would only repeat it.
function shownByPage(job, pathname) {
  // Find Candidates shows its own searches (progress screen, Your searches).
  if (job.kind === "find_candidates") return /^\/find-candidates(\/|$)/.test(pathname);
  // A company's Research page carries its own "Building profile…" state.
  if (job.kind === "profile")
    return !!job.link && decodePath(job.link) === decodePath(pathname);
  return false;
}


export default function ActivityTray() {
  const { jobs } = useActivity();
  const { activeRuns } = useFindNotification();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // A search's role title (client data, so the activity feed has only "People search"): read off the running searches,
  // and kept for the session so a search that just finished still shows its name.
  const titlesRef = useRef(new Map());
  for (const r of activeRuns || []) if (r.jobId && r.title) titlesRef.current.set(r.jobId, r.title);
  const [expanded, setExpanded] = useState(false);
  const [, setTick] = useState(0);
  const wrapRef = useRef(null);
  const onDashboard = pathname === "/";

  // An open list keeps finished jobs around; a closed lens clears them quickly.
  const keepMs = expanded ? RECENT_MS : DONE_MS;
  const now = Date.now();
  const shown = jobs.filter((j) => {
    if (shownByPage(j, pathname)) return false;
    if (isActive(j)) return true;
    return j.finishedAt && now - new Date(j.finishedAt).getTime() < keepMs;
  });

  // Polling stops once nothing runs, so re-render when the next finished job is due to clear.
  const nextExpiry = shown
    .filter((j) => !isActive(j))
    .map((j) => new Date(j.finishedAt).getTime() + keepMs - now)
    .reduce((a, b) => Math.min(a, b), Infinity);
  useEffect(() => {
    if (!Number.isFinite(nextExpiry)) return undefined;
    const t = setTimeout(() => setTick((n) => n + 1), Math.max(250, nextExpiry + 50));
    return () => clearTimeout(t);
  }, [nextExpiry]);

  // Clicking outside the lens and list, or pressing Escape, closes the list.
  useEffect(() => {
    if (!expanded) return undefined;
    const onDown = (e) => wrapRef.current && !wrapRef.current.contains(e.target) && setExpanded(false);
    const onKey = (e) => e.key === "Escape" && setExpanded(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [expanded]);

  useEffect(() => {
    if (shown.length === 0 && expanded) setExpanded(false);
  }, [shown.length, expanded]);

  if (onDashboard || shown.length === 0) return null;

  const active = shown.filter(isActive);
  const look = active.length
    ? "running"
    : shown.some((j) => j.status === "failed")
      ? "failed"
      : shown.some((j) => j.status === "paused")
        ? "waiting"
        : "done";
  // The ring fills with the running jobs' combined progress; it orbits when none of them reports a count.
  const counted = active.filter((j) => pctOf(j) != null && j.status !== "queued");
  const ringPct = counted.length ? Math.round(counted.reduce((a, j) => a + pctOf(j), 0) / counted.length) : null;
  const summary = active.length
    ? `${active.length} running${ringPct != null ? `, ${ringPct}% done` : ""}`
    : { failed: "Something didn't finish", waiting: "Waiting for you", done: "All done" }[look];
  const open = (link) => {
    setExpanded(false);
    navigate(link);
  };

  return (
    <div ref={wrapRef}>
      {expanded && (
        <Panel role="dialog" aria-label="Activity">
          <PanelHead $look={look}>
            <h2>Activity</h2>
            <span className="pill">{active.length ? `${active.length} running` : summary}</span>
            <button type="button" className="close" onClick={() => setExpanded(false)} aria-label="Close">
              <FiX size={16} />
            </button>
          </PanelHead>
          <PanelList>
            {[
              ["In progress", shown.filter((j) => isActive(j) || j.status === "paused")],
              ["Recently finished", shown.filter((j) => !isActive(j) && j.status !== "paused")],
            ]
              .filter(([, list]) => list.length)
              .map(([name, list]) => (
                <section key={name} aria-label={name}>
                  <Group>{name}</Group>
                  {list.map((j) => (
                    <TrayItem key={j.id} job={j} title={titlesRef.current.get(j.id) || ""} onOpen={open} />
                  ))}
                </section>
              ))}
          </PanelList>
        </Panel>
      )}
      <Fab
        type="button"
        $look={look}
        $open={expanded}
        onClick={() => setExpanded((v) => !v)}
        title={expanded ? "Hide activity" : summary}
        aria-label={`Activity: ${summary}`}
        aria-expanded={expanded}
      >
        <LensMark look={look} pct={ringPct} />
        {active.length > 1 && <span className="badge">{active.length}</span>}
        {look === "failed" && (
          <span className="badge" aria-hidden="true">
            !
          </span>
        )}
        {look === "done" && (
          <span className="badge" aria-hidden="true">
            <FiCheck size={11} />
          </span>
        )}
        <SrOnly role="status" aria-live="polite">
          {summary}
        </SrOnly>
      </Fab>
    </div>
  );
}
